import dayjs, { type Dayjs } from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  LinearProgress,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  type SxProps,
  type Theme,
} from '@mui/material';
import { Clear, Download, Refresh } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useTranslation } from 'react-i18next';

import FilteredListLayout from '../FilteredListLayout';
import LinkPagination from '../LinkPagination';
import ListActionButton from '../ListActionButton';
import { useWebSocket } from '../../contexts/WebSocketContext';
import { useCallReportWebSocket, type CallReportObserverEvent } from '../../hooks/useCallReportWebSocket';
import { useLanguage } from '../../hooks/useLanguage';
import { useListSearchParams } from '../../hooks/useListSearchParams';
import { useNumberFormat } from '../../hooks/useNumberFormat';
import { fetchCallReports, requestCallReportDownload } from '../../services/reports';
import { formatDateTime } from '../../utils/date';

import type {
  CallReportFilterMetadata,
  CallReportFilterMetadataEntry,
  CallReportListItem,
  CallReportStatus,
} from '../../types';


interface ReportStatusOption {
  value: CallReportStatus;
  label: string;
}

type ReportDateRangePreset = 'today' | 'yesterday' | 'last_7_days' | 'period';

const REPORT_STATUS_VALUES: readonly CallReportStatus[] = ['pending', 'processing', 'completed', 'failed'];
const REPORT_MULTI_VALUE_SEPARATOR = ',';
const FILTER_MIN_WIDTH_STATUS = 240;
const FILTER_MIN_WIDTH_DATE_RANGE = 220;
const DATE_FILTER_PARAM_FORMAT = 'YYYY-MM-DD';
const DATE_TIME_FILTER_PARAM_FORMAT = 'YYYY-MM-DDTHH:mm:ss';
const REPORT_DATE_RANGE_PRESETS: readonly ReportDateRangePreset[] = ['today', 'yesterday', 'last_7_days', 'period'];

interface ReportDateRangeOption {
  value: ReportDateRangePreset;
  label: string;
}

function buildFilterFieldSx(minWidth: number): SxProps<Theme> {
  return {
    minWidth,
    flex: `1 1 ${minWidth}px`,
  };
}

function isReportDateRangePreset(value: string | null | undefined): value is ReportDateRangePreset {
  if (!value) {
    return false;
  }
  return REPORT_DATE_RANGE_PRESETS.includes(value as ReportDateRangePreset);
}

function parseMultiFilterValue(rawValue: string | null | undefined): string[] {
  if (!rawValue) {
    return [];
  }
  const normalizedValues = rawValue
    .split(REPORT_MULTI_VALUE_SEPARATOR)
    .map((value) => value.trim())
    .filter(Boolean);
  return Array.from(new Set(normalizedValues));
}

function serializeMultiFilterValue(values: string[]): string | undefined {
  const normalizedValues = values
    .map((value) => value.trim())
    .filter(Boolean);
  if (normalizedValues.length === 0) {
    return undefined;
  }
  return normalizedValues.join(REPORT_MULTI_VALUE_SEPARATOR);
}

function getReportStatusColor(
  status: CallReportStatus,
): 'default' | 'warning' | 'success' | 'error' {
  if (status === 'completed') {
    return 'success';
  }
  if (status === 'failed') {
    return 'error';
  }
  if (status === 'processing') {
    return 'warning';
  }
  return 'default';
}

function applyReportEvent(
  report: CallReportListItem,
  event: CallReportObserverEvent,
): CallReportListItem {
  return {
    ...report,
    status: event.status,
    name: event.name ?? report.name,
    description: event.description ?? report.description,
    createdByName: event.created_by_name ?? report.createdByName,
    startedAt: event.started_at ?? report.startedAt,
    completedAt: event.completed_at ?? report.completedAt,
    fileName: event.file_name ?? report.fileName,
    fileSize: event.file_size ?? report.fileSize,
    totalCalls: event.total_calls ?? report.totalCalls,
    progress: event.progress ?? report.progress,
    processedCalls: event.processed_calls ?? report.processedCalls,
    estimatedSecondsRemaining: event.estimated_seconds_remaining ?? report.estimatedSecondsRemaining,
    progressMessage: event.progress_message ?? report.progressMessage,
    errorMessage: event.error_message ?? report.errorMessage,
    downloadUrl: event.file_url ?? report.downloadUrl,
    filtersMetadata: (event.filters_metadata as CallReportFilterMetadata | undefined) ?? report.filtersMetadata,
  };
}

function CallReportLiveObserver({
  reportId,
  onEvent,
  onReady,
  onFailed,
}: {
  reportId: string;
  onEvent: (event: CallReportObserverEvent) => void;
  onReady: (event: CallReportObserverEvent) => void;
  onFailed: (event: CallReportObserverEvent) => void;
}) {
  useCallReportWebSocket(reportId, {
    onReportUpdated: onEvent,
    onReportReady: onReady,
    onReportFailed: onFailed,
  });
  return null;
}

const REPORT_BODY_CELL_SX: SxProps<Theme> = {
  verticalAlign: 'middle',
};

const REPORT_DOWNLOAD_HEADER_CELL_SX: SxProps<Theme> = {
  textAlign: 'center',
};

const REPORT_DOWNLOAD_CELL_SX: SxProps<Theme> = {
  ...REPORT_BODY_CELL_SX,
  textAlign: 'center',
};

const REPORT_DOWNLOAD_BOX_SX: SxProps<Theme> = {
  display: 'inline-flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 0.5,
};

export default function CallReportsTab() {
  const { t } = useTranslation(['pages', 'common']);
  const { language } = useLanguage();
  const { state: webSocketState } = useWebSocket();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const tableRowHoverBg = alpha(theme.palette.primary.main, 0.08);
  const reportHeaderRowSx: SxProps<Theme> = useMemo(() => ({
    bgcolor: alpha(theme.palette.primary.main, 0.08),
    '& th': {
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      fontSize: '0.75rem',
      whiteSpace: 'nowrap',
      textAlign: 'left',
      verticalAlign: 'middle',
    },
  }), [theme]);
  const dayjsLocale = useMemo(() => {
    const localeMap: Record<string, string> = {
      'pt-BR': 'pt-br',
      en: 'en',
      es: 'es',
    };
    return localeMap[language] || 'en';
  }, [language]);
  const filterInputSx = {
    borderRadius: '10px',
    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.04)' : '#ffffff',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
    },
    '& .MuiSelect-select': {
      padding: '11px 12px',
    },
    '& .MuiInputBase-input': {
      padding: '11px 12px',
    },
    '& .MuiPickersInputBase-root': {
      padding: '3px 14px 2px 14px',
    },
  };
  const { formatNumber, formatDecimal } = useNumberFormat();

  const [reports, setReports] = useState<CallReportListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalReports, setTotalReports] = useState(0);
  const [downloadingReportId, setDownloadingReportId] = useState<string | null>(null);

  const {
    filters,
    page,
    rowsPerPage,
    ordering,
    setParams,
    setPage,
    setRowsPerPage,
    getPageLink,
  } = useListSearchParams({
    filters: ['status', 'date_range', 'start_date', 'end_date'],
    defaultPage: 1,
    defaultPageSize: 25,
    defaultOrdering: '-created_at',
  });

  const statusFilterValues = useMemo(() => parseMultiFilterValue(filters.status), [filters.status]);
  const startDateFilterValue = filters.start_date ?? '';
  const endDateFilterValue = filters.end_date ?? '';
  const selectedDateRange = useMemo<ReportDateRangePreset | null>(() => {
    if (isReportDateRangePreset(filters.date_range)) {
      return filters.date_range;
    }
    if (startDateFilterValue || endDateFilterValue) {
      return 'period';
    }
    return null;
  }, [endDateFilterValue, filters.date_range, startDateFilterValue]);
  const dateRangeOptions = useMemo<ReportDateRangeOption[]>(
    () => [
      { value: 'today', label: t('pages:calls.filters.today') },
      { value: 'yesterday', label: t('pages:calls.filters.yesterday') },
      { value: 'last_7_days', label: t('pages:calls.filters.last7Days') },
      { value: 'period', label: t('pages:calls.filters.period') },
    ],
    [t],
  );
  const dateRangeOption = useMemo(
    () => dateRangeOptions.find((option) => option.value === selectedDateRange) ?? null,
    [dateRangeOptions, selectedDateRange],
  );
  const startDateValue = useMemo<Dayjs | null>(() => {
    if (!startDateFilterValue) {
      return null;
    }
    const value = dayjs(startDateFilterValue);
    return value.isValid() ? value : null;
  }, [startDateFilterValue]);
  const endDateValue = useMemo<Dayjs | null>(() => {
    if (!endDateFilterValue) {
      return null;
    }
    const value = dayjs(endDateFilterValue);
    return value.isValid() ? value : null;
  }, [endDateFilterValue]);
  const resolvedDateParams = useMemo(() => {
    if (selectedDateRange === 'period') {
      return {
        startDate: startDateFilterValue || undefined,
        endDate: endDateFilterValue || undefined,
      };
    }
    if (!selectedDateRange) {
      return {
        startDate: undefined,
        endDate: undefined,
      };
    }

    const today = dayjs();
    if (selectedDateRange === 'today') {
      const date = today.format(DATE_FILTER_PARAM_FORMAT);
      return { startDate: date, endDate: date };
    }
    if (selectedDateRange === 'yesterday') {
      const date = today.subtract(1, 'day').format(DATE_FILTER_PARAM_FORMAT);
      return { startDate: date, endDate: date };
    }

    return {
      startDate: today.subtract(6, 'day').format(DATE_FILTER_PARAM_FORMAT),
      endDate: today.format(DATE_FILTER_PARAM_FORMAT),
    };
  }, [endDateFilterValue, selectedDateRange, startDateFilterValue]);

  const statusOptions = useMemo<ReportStatusOption[]>(
    () => REPORT_STATUS_VALUES.map((value) => ({
      value,
      label: t(`pages:reports.calls.statusLabels.${value}`),
    })),
    [t],
  );

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchCallReports({
        page: page + 1,
        pageSize: rowsPerPage,
        ordering: ordering || '-created_at',
        status: serializeMultiFilterValue(statusFilterValues),
        startDate: resolvedDateParams.startDate,
        endDate: resolvedDateParams.endDate,
      });
      setReports(response.results);
      setTotalReports(response.count);
    } catch (error) {
      console.error('Error loading call reports:', error);
      setReports([]);
      setTotalReports(0);
    } finally {
      setLoading(false);
    }
  }, [ordering, page, resolvedDateParams.endDate, resolvedDateParams.startDate, rowsPerPage, statusFilterValues]);

  useEffect(() => {
    void fetchReports();
  }, [fetchReports]);

  const handleLiveEvent = useCallback((event: CallReportObserverEvent) => {
    setReports((currentReports) => {
      const existingReport = currentReports.find((report) => report.id === event.id);
      if (!existingReport) {
        return currentReports;
      }
      return currentReports.map((report) => (
        report.id === event.id
          ? applyReportEvent(report, event)
          : report
      ));
    });
  }, []);

  const handleLiveTerminal = useCallback((event: CallReportObserverEvent) => {
    handleLiveEvent(event);
    void fetchReports();
  }, [fetchReports, handleLiveEvent]);

  const handleDownload = useCallback(async (reportId: string) => {
    try {
      setDownloadingReportId(reportId);
      const downloadUrl = await requestCallReportDownload(reportId);
      if (downloadUrl) {
        window.open(downloadUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      console.error('Error requesting call report download:', error);
    } finally {
      setDownloadingReportId((current) => (current === reportId ? null : current));
    }
  }, []);

  const activeReportIds = useMemo(
    () => reports
      .filter((report) => report.status === 'pending' || report.status === 'processing')
      .map((report) => report.id),
    [reports],
  );
  const activeReportIdsSignature = useMemo(
    () => activeReportIds.join(','),
    [activeReportIds],
  );
  const reconciledActiveReportSignatureRef = useRef('');

  useEffect(() => {
    if (!webSocketState.isConnected || activeReportIds.length === 0) {
      if (activeReportIds.length === 0) {
        reconciledActiveReportSignatureRef.current = '';
      }
      return;
    }
    if (reconciledActiveReportSignatureRef.current === activeReportIdsSignature) {
      return;
    }
    reconciledActiveReportSignatureRef.current = activeReportIdsSignature;
    void fetchReports();
  }, [activeReportIds.length, activeReportIdsSignature, fetchReports, webSocketState.isConnected]);

  const renderFilterValues = useCallback((entry: CallReportFilterMetadataEntry) => {
    const translatedValues = entry.values.map((item) => {
      const rawValue = item.display || item.value;
      if (entry.key === 'status') {
        return t(`pages:reports.calls.statusLabels.${item.value}`, rawValue);
      }
      if (entry.key === 'outcome') {
        return t(`pages:calls.outcomeLabels.${item.value}`, rawValue);
      }
      if (entry.key === 'region') {
        return t(`pages:calls.filters.regionOptions.${item.value}`, rawValue);
      }
      if (entry.key === 'phone_type') {
        return t(`pages:calls.filters.phoneTypeOptions.${item.value}`, rawValue);
      }
      if (
        entry.key === 'has_transfer'
        || entry.key === 'has_human_answer'
        || entry.key === 'has_ai_answer'
        || entry.key === 'has_ai_refund'
      ) {
        return item.value === 'true' ? t('common:common.yes') : t('common:common.no');
      }
      return rawValue;
    });
    return translatedValues.join(' • ');
  }, [t]);

  const renderFilterLabel = useCallback((entryKey: string) => {
    if (entryKey === 'period') {
      return t('pages:calls.filters.dateRangeLabel');
    }
    if (entryKey === 'campaign') {
      return t('pages:calls.filters.campaignLabel');
    }
    if (entryKey === 'status') {
      return t('pages:calls.filters.statusLabel');
    }
    if (entryKey === 'outcome') {
      return t('pages:calls.filters.outcomeLabel');
    }
    if (entryKey === 'has_transfer') {
      return t('pages:calls.filters.transferLabel');
    }
    if (entryKey === 'has_human_answer') {
      return t('pages:calls.filters.humanAnswerLabel');
    }
    if (entryKey === 'has_ai_answer') {
      return t('pages:calls.filters.aiAnswerLabel');
    }
    if (entryKey === 'has_ai_refund') {
      return t('pages:calls.filters.aiRefundLabel');
    }
    if (entryKey === 'ivr_selected_digit') {
      return t('pages:calls.filters.ivrSelectedDigitLabel');
    }
    if (entryKey === 'participant_user') {
      return t('pages:calls.filters.participantLabel');
    }
    if (entryKey === 'region') {
      return t('pages:calls.filters.regionLabel');
    }
    if (entryKey === 'state_code') {
      return t('pages:calls.filters.stateLabel');
    }
    if (entryKey === 'ddd') {
      return t('pages:calls.filters.dddLabel');
    }
    if (entryKey === 'phone_type') {
      return t('pages:calls.filters.phoneTypeLabel');
    }
    if (entryKey === 'search') {
      return t('pages:reports.calls.filters.usedSearchLabel');
    }
    return entryKey;
  }, [t]);

  const renderPeriodValue = useCallback((entry: CallReportFilterMetadataEntry) => {
    const startValue = entry.startDate;
    const endValue = entry.endDate;
    if (!startValue || !endValue) {
      return renderFilterValues(entry);
    }
    const start = dayjs(startValue);
    const end = dayjs(endValue);
    if (!start.isValid() || !end.isValid()) {
      return renderFilterValues(entry);
    }
    return `${start.locale(dayjsLocale).format('L')} → ${end.locale(dayjsLocale).format('L')}`;
  }, [dayjsLocale, renderFilterValues]);

  const renderFiltersUsed = useCallback((filtersMetadata: CallReportFilterMetadata | null | undefined) => {
    const entries = filtersMetadata?.entries ?? [];
    if (entries.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          {t('pages:reports.calls.noFiltersMetadata')}
        </Typography>
      );
    }
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {entries.map((entry) => (
          <Box key={`${entry.key}-${entry.values.map((value) => value.value).join('-')}`} sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            <Typography variant="caption" color="text.secondary">
              {renderFilterLabel(entry.key)}
            </Typography>
            <Typography variant="body2" sx={{ lineHeight: 1.35 }}>
              {entry.key === 'period' ? renderPeriodValue(entry) : renderFilterValues(entry)}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  }, [renderFilterLabel, renderFilterValues, renderPeriodValue, t]);

  const handleClearFilters = useCallback(() => {
    setParams(
      {
        status: null,
        date_range: null,
        start_date: null,
        end_date: null,
      },
      { resetPage: true },
    );
  }, [setParams]);

  const filterControls = (
    <>
      <Autocomplete<ReportStatusOption, true, false, false>
        multiple
        disableCloseOnSelect
        options={statusOptions}
        value={statusOptions.filter((option) => statusFilterValues.includes(option.value))}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        getOptionLabel={(option) => option.label}
        onChange={(_, selectedOptions) => {
          setParams(
            { status: serializeMultiFilterValue(selectedOptions.map((option) => option.value)) },
            { resetPage: true },
          );
        }}
        renderOption={(props, option, { selected }) => (
          <Box component="li" {...props}>
            <Checkbox size="small" sx={{ mr: 1 }} checked={selected} />
            {option.label}
          </Box>
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.value}
              size="small"
              label={option.label}
            />
          ))
        }
        sx={buildFilterFieldSx(FILTER_MIN_WIDTH_STATUS)}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            label={t('pages:reports.calls.filters.status')}
            placeholder={t('pages:reports.calls.filters.statusPlaceholder')}
            sx={filterInputSx}
          />
        )}
      />
      <Autocomplete<ReportDateRangeOption, false, false, false>
        options={dateRangeOptions}
        value={dateRangeOption}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        getOptionLabel={(option) => option.label}
        onChange={(_, selectedOption) => {
          const nextDateRange = selectedOption?.value ?? null;
          if (!nextDateRange) {
            setParams({ date_range: null, start_date: null, end_date: null }, { resetPage: true });
            return;
          }
          if (nextDateRange === 'period') {
            setParams({ date_range: nextDateRange }, { resetPage: true });
            return;
          }
          setParams({ date_range: nextDateRange, start_date: null, end_date: null }, { resetPage: true });
        }}
        sx={buildFilterFieldSx(FILTER_MIN_WIDTH_DATE_RANGE)}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            label={t('pages:calls.filters.dateRangeLabel')}
            sx={filterInputSx}
          />
        )}
      />
      {selectedDateRange === 'period' && (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjsLocale}>
          <>
            <DateTimePicker
              label={t('pages:calls.filters.fromDate')}
              value={startDateValue}
              onChange={(value) => {
                const formattedDate = value && value.isValid() ? value.format(DATE_TIME_FILTER_PARAM_FORMAT) : null;
                setParams({ start_date: formattedDate }, { resetPage: true });
              }}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: buildFilterFieldSx(FILTER_MIN_WIDTH_DATE_RANGE),
                },
              }}
            />
            <DateTimePicker
              label={t('pages:calls.filters.toDate')}
              value={endDateValue}
              onChange={(value) => {
                const formattedDate = value && value.isValid() ? value.format(DATE_TIME_FILTER_PARAM_FORMAT) : null;
                setParams({ end_date: formattedDate }, { resetPage: true });
              }}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: buildFilterFieldSx(FILTER_MIN_WIDTH_DATE_RANGE),
                },
              }}
            />
          </>
        </LocalizationProvider>
      )}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', ml: 'auto' }}>
        <ListActionButton variant="outlined" startIcon={<Refresh />} onClick={() => void fetchReports()}>
          {t('common:actions.refresh')}
        </ListActionButton>
        <ListActionButton variant="outlined" startIcon={<Clear />} onClick={handleClearFilters}>
          {t('common:actions.clear')}
        </ListActionButton>
      </Box>
    </>
  );

  const tableContent = (
    <Box sx={{ position: 'relative' }}>
      {activeReportIds.map((reportId) => (
        <CallReportLiveObserver
          key={reportId}
          reportId={reportId}
          onEvent={handleLiveEvent}
          onReady={handleLiveTerminal}
          onFailed={handleLiveTerminal}
        />
      ))}
      <Table>
        <TableHead>
          <TableRow sx={reportHeaderRowSx}>
            <TableCell>{t('pages:reports.calls.columns.name')}</TableCell>
            <TableCell>{t('pages:reports.calls.columns.filtersUsed')}</TableCell>
            <TableCell>{t('pages:reports.calls.columns.createdAt')}</TableCell>
            <TableCell>{t('pages:reports.calls.columns.createdBy')}</TableCell>
            <TableCell>{t('pages:reports.calls.columns.volume')}</TableCell>
            <TableCell>{t('pages:reports.calls.columns.status')}</TableCell>
            <TableCell sx={REPORT_DOWNLOAD_HEADER_CELL_SX}>{t('pages:reports.calls.columns.download')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            Array.from({ length: 5 }).map((_, index) => (
              <TableRow key={`call-report-skeleton-${index}`}>
                {Array.from({ length: 7 }).map((__, cellIndex) => (
                  <TableCell
                    key={cellIndex}
                    sx={cellIndex === 6 ? REPORT_DOWNLOAD_CELL_SX : REPORT_BODY_CELL_SX}
                  >
                    <Skeleton variant="text" width={cellIndex === 1 ? '90%' : '75%'} />
                    <Skeleton variant="text" width={cellIndex === 1 ? '60%' : '40%'} />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : reports.length > 0 ? (
            reports.map((report) => (
              <TableRow
                key={report.id}
                hover
                sx={{
                  '&:hover': {
                    backgroundColor: tableRowHoverBg,
                  },
                }}
              >
                <TableCell sx={{ ...REPORT_BODY_CELL_SX, minWidth: 260 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.35 }}>
                      {report.name}
                    </Typography>
                    {report.description && (
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                        {report.description}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ ...REPORT_BODY_CELL_SX, minWidth: 280 }}>
                  {renderFiltersUsed(report.filtersMetadata)}
                </TableCell>
                <TableCell sx={{ ...REPORT_BODY_CELL_SX, minWidth: 180 }}>
                  <Typography variant="body2">
                    {formatDateTime(report.createdAt, { locale: language })}
                  </Typography>
                  {report.completedAt && (
                    <Typography variant="caption" color="text.secondary">
                      {t('pages:reports.calls.completedAt', {
                        value: formatDateTime(report.completedAt, { locale: language }),
                      })}
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={{ ...REPORT_BODY_CELL_SX, minWidth: 140 }}>
                  <Typography variant="body2">
                    {report.createdByName || t('pages:reports.calls.unknownUser')}
                  </Typography>
                </TableCell>
                <TableCell sx={{ ...REPORT_BODY_CELL_SX, minWidth: 160 }}>
                  <Typography variant="body2">
                    {t('pages:reports.calls.volumeValue', {
                      calls: formatNumber(report.totalCalls ?? 0),
                    })}
                  </Typography>
                  {(report.status === 'pending' || report.status === 'processing') && report.processedCalls !== null && (
                    <Typography variant="caption" color="text.secondary">
                      {t('pages:reports.calls.processedValue', {
                        calls: formatNumber(report.processedCalls ?? 0),
                      })}
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={{ ...REPORT_BODY_CELL_SX, minWidth: 190 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                    <Chip
                      size="small"
                      color={getReportStatusColor(report.status)}
                      label={t(`pages:reports.calls.statusLabels.${report.status}`)}
                      sx={{ width: 'fit-content' }}
                    />
                    {(report.status === 'pending' || report.status === 'processing') && (
                      <>
                        <LinearProgress
                          variant={report.progress !== null ? 'determinate' : 'indeterminate'}
                          value={report.progress ?? 0}
                          sx={{ borderRadius: 999 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {report.progress !== null ? formatDecimal(report.progress, 2) : '0.00'}%
                        </Typography>
                        {report.estimatedSecondsRemaining !== null && report.estimatedSecondsRemaining !== undefined && (
                          <Typography variant="caption" color="text.secondary">
                            {t('pages:reports.calls.eta', { seconds: formatNumber(report.estimatedSecondsRemaining) })}
                          </Typography>
                        )}
                        {report.progressMessage && (
                          <Typography variant="caption" color="text.secondary">
                            {report.progressMessage}
                          </Typography>
                        )}
                      </>
                    )}
                    {report.status === 'failed' && report.errorMessage && (
                      <Typography variant="caption" color="error.main">
                        {report.errorMessage}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ ...REPORT_DOWNLOAD_CELL_SX, minWidth: 140 }}>
                  {report.downloadUrl ? (
                    <Box sx={REPORT_DOWNLOAD_BOX_SX}>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<Download />}
                        disabled={downloadingReportId === report.id}
                        onClick={() => {
                          void handleDownload(report.id);
                        }}
                      >
                        {t('common:actions.download')}
                      </Button>
                      {report.fileSize !== null && report.fileSize !== undefined && (
                        <Typography variant="caption" color="text.secondary">
                          {t('pages:reports.calls.fileSize', {
                            size: formatDecimal(report.fileSize / (1024 * 1024), 2),
                          })}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                      {t('pages:reports.calls.noDownload')}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={7} sx={REPORT_BODY_CELL_SX}>
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                  {t('pages:reports.calls.empty')}
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );

  const pagination = (
    <LinkPagination
      component="div"
      count={totalReports}
      page={page}
      rowsPerPage={rowsPerPage}
      onPageChange={(_, nextPage) => setPage(nextPage)}
      onRowsPerPageChange={(event) => setRowsPerPage(Number(event.target.value))}
      labelRowsPerPage={t('common:pagination.rowsPerPage')}
      labelDisplayedRows={({ from, to, count }) =>
        count !== -1
          ? `${from}-${to} ${t('common:common.of')} ${count}`
          : `${from}-${to} ${t('common:common.of')} ${t('common:common.total')} >${to}`
      }
      getItemAriaLabel={(type) => {
        if (type === 'first') return t('common:pagination.firstPage');
        if (type === 'last') return t('common:pagination.lastPage');
        if (type === 'next') return t('common:pagination.nextPage');
        return t('common:pagination.previousPage');
      }}
      getPageLink={getPageLink}
      rowsPerPageOptions={[10, 25, 50]}
    />
  );

  return (
    <FilteredListLayout
      filterControls={filterControls}
      table={tableContent}
      tableFooter={!loading && reports.length > 0 ? pagination : undefined}
    />
  );
}
